import React, { createContext, useEffect, useState } from 'react';
import { LanguageCode, LanguageContextType, TranslationConfig, SUPPORTED_LANGUAGES, LanguageSession } from '../models/language';
import { MSGItemBody } from '../models/msg';
import { translateText } from '../services/google-translate';
import { SDKHumanParticipantType, SDKParticipantType } from '../models/sdk';
import { ProviderParams } from '../models/react';
import { UserContextType, UserDefaultType } from '../models/user';
import useConfig from '../hooks/config.hook';
import useSessionStorage from '../hooks/sessionStorage.hook';
import { useEngagementContext } from './engagement';

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);
export function useLanguageContext() {
    return React.useContext(LanguageContext);
}

export default function LanguageProvider({ children }: ProviderParams) {

    const getAgentLanguageURL = `${useConfig().url.api.engagementLanguage}`
    const saveTranscriptUrl = `${useConfig().url.api.saveTranscript}`
    const translateUrl = `${useConfig().url.gTranslate.base}`
    const languageStorage = useSessionStorage("language")
    const [AgentLanguage, setAgentLanguage] = useState<LanguageCode>(null);
    const [UserLanguage, setUserLanguage] = useState<LanguageCode>(null);
    const [status, setStatus] = useState<boolean>(false)
    useEffect(()=>{
        reloadSessionStorage()
     }, [])
    

    useEffect(()=>{
        if (AgentLanguage || UserLanguage || status) {
            let languageSession:LanguageSession = {
                AgentLanguage: AgentLanguage,
                UserLanguage: UserLanguage,
                status: status
            }
            languageStorage.setValue(JSON.stringify(languageSession))
        }
    }, [AgentLanguage, UserLanguage, status])
    function reloadSessionStorage() {
        const languageObj =  recoverLanguageFromStorage()
        setAgentLanguage(languageObj.AgentLanguage)
        setUserLanguage(languageObj.UserLanguage)
        setStatus(languageObj.status)
    }
    function recoverLanguageFromStorage (): LanguageSession {
        let languageStored:LanguageSession  = {
            AgentLanguage:null,
            UserLanguage:null, 
            status: false
        }
        if (languageStorage.value && languageStorage.value!=="") {
            try{
                const stored = JSON.parse(languageStorage.value) as LanguageSession
                languageStored = stored;

            }catch(err:any){
                console.debug("Error during parse language context values from session storage")
            }
        }
        return languageStored
    }
    const translateMessage = async (message: string | MSGItemBody, sender: SDKHumanParticipantType): Promise<string> => {
        return translateMessageWithLanguages(message, sender, UserLanguage, AgentLanguage)
    }
    async function saveTranscript(message:string, messageId: string, from:SDKHumanParticipantType, user:UserDefaultType, engagementId: string) {
        try {
            const response = await fetch(saveTranscriptUrl, {
                method: "POST",
                body: JSON.stringify({
                    "body": message,
                    "engagementId": engagementId,
                    "from": from,
                    "messageId":`${messageId}`,
                    "userIdentifier": user.firstName + " " + user.lastName
                }),
                headers: {
                    "Content-Type": "application/json; charset=UTF-8"
                }
            });
            if (!response.ok) {
                throw new Error(`HTTP error! Status: ${response.status}`);
            }
            const data = await response.json();
            return data
        } catch (error:any) {
            console.error('Error:', error.message);
        }
    }
    function getTranslationConfig(sender: SDKHumanParticipantType, agentLanguage: LanguageCode, userLanguage: LanguageCode): TranslationConfig | null {
        if (sender === SDKParticipantType.AGENT) {
            return {
                sourceLanguage: agentLanguage,
                targetLanguage: userLanguage
            }
        }
        if (sender === SDKParticipantType.CUSTOMER) {
            return {
                sourceLanguage: userLanguage,
                targetLanguage: agentLanguage
            }
        }
    
        return null
    
    }

    const translateMessageWithLanguages = async (message: string | MSGItemBody, sender: SDKHumanParticipantType, userLanguage: LanguageCode, agentLanguage: LanguageCode) => {
        const config = getTranslationConfig(sender, agentLanguage, userLanguage)
        if (agentLanguage && userLanguage && agentLanguage !== userLanguage && typeof message === 'string' && config) {
            const translation = await translateText(translateUrl, message, config)
            return translation
        } else {
            return ''
        }
    }
    const getLanguageByString = (message: string): LanguageCode | null => {
        const language = SUPPORTED_LANGUAGES.find((elem) => (elem.displayName === message || elem.apiKeyName === message ))
        if (language && language.languageCode) return language.languageCode as LanguageCode
        return null
    }
    function resetLanguage() {
        setAgentLanguage(null)
        setUserLanguage(null)
        setStatus(false)
        languageStorage.removeValue()
    }
    async function getAgentLanguage(engagementId:string) {
        try {
            const response = await fetch(getAgentLanguageURL, {method : "POST", body: JSON.stringify({
                "engagementId": engagementId
              })});
            if (!response.ok) {
                throw new Error(`HTTP error! Status: ${response.status}`);
            }
            const data = await response.json();
            return data
        } catch (error:any) {
            console.error('Error:', error.message);
        }
    }


    return (
        <LanguageContext.Provider value={{ status, setStatus, AgentLanguage , UserLanguage, getAgentLanguage, setAgentLanguage, setUserLanguage, translateMessage, getLanguageByString, translateMessageWithLanguages, saveTranscript, resetLanguage }}>
            {children}
        </LanguageContext.Provider>
    );

};

